import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";

const STATUSES = ["new", "preparing", "shipped", "delivered", "unavailable"];

export function SampleManageDialog({ sample, open, onOpenChange, onSaved }) {
  const { t } = useLocale();
  const [form, setForm] = useState({
    status: sample?.status || "new",
    tracking_number: sample?.tracking_number || "",
    tracking_carrier: sample?.tracking_carrier || "",
    tracking_url: sample?.tracking_url || "",
    notes: sample?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!sample) return;
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        tracking_number: form.tracking_number || null,
        tracking_carrier: form.tracking_carrier || null,
        tracking_url: form.tracking_url || null,
        notes: form.notes || null,
      };
      await api.patch(`/interests/${sample.id}`, payload);
      toast.success(t("samples.toast_updated"));
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-lg">
        <DialogHeader>
          <DialogTitle data-testid="sample-manage-title" className="font-display text-2xl tracking-tight">{t("samples.manage_title")}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {t("samples.manage_subtitle")}
          </DialogDescription>
        </DialogHeader>

        {sample && (
          <div className="border border-border bg-secondary/30 p-3 mb-2 font-mono-data text-[11px] space-y-1">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground uppercase tracking-wider text-[10px]">{t("samples.col_lot")}</span>
              <span className="text-right">#{sample.lot_id?.slice(-6)} · {sample.bags_quantity} {t("common.bags")}</span>
            </div>
            {sample.shipping_address && (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground uppercase tracking-wider text-[10px]">{t("samples.col_shipping")}</span>
                <span className="text-right text-[11px] max-w-[260px] truncate">
                  {sample.shipping_recipient ? `${sample.shipping_recipient} · ` : ""}
                  {sample.shipping_address}, {sample.shipping_city} · {sample.shipping_country}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("samples.field_status")}
            </Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="sample-manage-status" className="mt-1.5 h-11 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`sample-manage-status-${s}`}>
                    {t(`samples.status_${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("samples.field_tracking_number")}
            </Label>
            <Input
              value={form.tracking_number}
              onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
              data-testid="sample-manage-tracking-number"
              placeholder="BR123456789XX"
              className="mt-1.5 h-11 rounded-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("samples.field_tracking_carrier")}
              </Label>
              <Input
                value={form.tracking_carrier}
                onChange={(e) => setForm({ ...form, tracking_carrier: e.target.value })}
                data-testid="sample-manage-tracking-carrier"
                placeholder="DHL · FedEx · Correios"
                className="mt-1.5 h-11 rounded-none"
              />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("samples.field_tracking_url")}
              </Label>
              <Input
                value={form.tracking_url}
                onChange={(e) => setForm({ ...form, tracking_url: e.target.value })}
                data-testid="sample-manage-tracking-url"
                placeholder="https://..."
                className="mt-1.5 h-11 rounded-none"
              />
            </div>
          </div>

          <div>
            <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("samples.field_internal_notes")}
            </Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="sample-manage-notes"
              rows={3}
              className="mt-1.5 rounded-none"
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="sample-manage-save"
            className="rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-wider"
          >
            {saving ? t("common.loading") : t("samples.save_btn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
